import React from 'react';
import { toImmutable, Immutable } from 'nuclear-js';
import CreateReactClass from 'create-react-class';

import Analytics from 'utils/analytics';
import CommonValues from 'resources/common-values';
import RuntimeActions from 'data/app-runtime/actions';

import ShareOptions from './share-options';
import BatchOptions from './batch-options/index';
import BatchUtils from './utils';

import PropTypes from 'prop-types';

import { DeepCompareComponentShouldUpdateMixin } from 'utils/react';

export default CreateReactClass({
    displayName: 'print-and-share',
    mixins: [DeepCompareComponentShouldUpdateMixin],

    propTypes: {
        isSingleStudentMode: PropTypes.bool,
        linkingStudies: PropTypes.instanceOf(Immutable.List).isRequired,
        reportsQueueLink: PropTypes.string.isRequired,
        selectedClass: PropTypes.instanceOf(Immutable.Map), // Typically required, but not in isSingleStudentMode
        selectedModule: PropTypes.string,
        selectedSubject: PropTypes.instanceOf(Immutable.Map),
        selectedTermNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        studentInfo: PropTypes.instanceOf(Immutable.List),
        students: PropTypes.instanceOf(Immutable.List).isRequired,
        termsWithTestEvents: PropTypes.instanceOf(Immutable.List),
    },

    getInitialState() {
        return {
            batchOptionState: false,
            batchTab: 'subjects',
            canToggle: true,
            isFamilyReport: false,
            selectedPrintFormat: Immutable.Set(),
            selectedStudentForPrinting: 'current',
            showBatchPrint: false,
            showPrintShare: false,
            studentPreviousSelection: 'current',
            visitedSections: Immutable.Set.of('subjects'), //Sections includes ('subjects', 'data', 'pages', 'students')
        };
    },

    componentDidMount() {
        document.addEventListener('keyup', this.handleKeyup);
    },

    componentWillUnmount() {
        document.removeEventListener('keyup', this.handleKeyup);
    },

    handleKeyup(event) {
        if ((event.key === 'Escape' || event.keyCode === 27) && this.state.showPrintShare && this.state.canToggle) {
            this.togglePrintAndShare();
        }
        this.setState({ canToggle: true });
    },

    isHelpBoxOpen(isExpanded) {
        if (isExpanded) {
            this.setState({ canToggle: false });
        }
    },

    togglePrintAndShare(event, printStatus = '') {
        /* Caret click: Closes all Print/Share
         ** Return to Main screen: reset batch options
         */
        Analytics.recordButtonClick(
            CommonValues.SectionIDs.printAndShare,
            this.state.showPrintShare ? 'close-module' : 'expand-module'
        );
        if (!this.state.showPrintShare) {
            this.setState({
                batchOptionState: true,
            });
        }

        if (this.state.showBatchPrint && printStatus === 'success') {
            this.setState({
                batchOptionState: false,
                batchTab: 'subjects',
                selectedPrintFormat: Immutable.Set(),
                selectedStudentForPrinting: 'current',
                studentPreviousSelection: 'current',
                visitedSections: Immutable.Set.of('subjects'),
            });
        }
        this.setState({
            batchTab: 'subjects',
            batchOptionState: false,
            isFamilyReport: false,
            selectedStudentForPrinting: 'current',
            showBatchPrint: false,
            showPrintShare: !this.state.showPrintShare,
            visitedSections: Immutable.Set.of('subjects'),
        });
        RuntimeActions.setPageAlert(
            Immutable.Map({
                messageContent: null,
                isActive: false,
                actionStatus: '',
            })
        );
    },

    closeBatchPrinting(event) {
        /* PRINT AND SHARE
         ** Return to Share Options: reset batch options
         */
        Analytics.recordButtonClick(CommonValues.SectionIDs.printAndShare, 'back-batch-pdf');

        this.setState({
            batchTab: 'subjects',
            batchOptionState: false,
            isFamilyReport: false,
            selectedStudentForPrinting: 'current',
            showBatchPrint: false,
            visitedSections: Immutable.Set.of('subjects'),
        });
    },

    setDefaultPrintFormatForFamilyReport() {
        // set 2 sided print to true (default)
        let format = this.state.selectedPrintFormat;
        format = format.add('page break').add('page break selected students');
        this.setState({
            selectedPrintFormat: format,
        });
    },

    // Share Options: Selecting Batch Report PDF
    selectBatchPrintPDF(origin) {
        let isFamilyReport = false;
        if (origin === 'family') {
            // Share Options: Selecting Family Report PDF
            isFamilyReport = true;
            this.setDefaultPrintFormatForFamilyReport();
        } else {
            // clear checkboxes for 2 sided print
            this.setState({
                selectedPrintFormat: Immutable.Set(),
            });
        }
        Analytics.recordButtonClick(
            CommonValues.SectionIDs.printAndShare,
            isFamilyReport ? 'start-family-pdf' : 'start-batch-pdf'
        );

        this.setState({
            batchTab: 'subjects',
            batchOptionState: true,
            isFamilyReport: isFamilyReport,
            showBatchPrint: !this.state.showBatchPrint,
        });
    },

    nextButtonAction(event, source) {
        const { isFamilyReport } = this.state;

        if (isFamilyReport && source === 'pages') {
            source = 'students';
        }

        const newVisitedSections = this.state.visitedSections.add(source);

        this.setState({
            batchTab: source,
            visitedSections: newVisitedSections,
        });
    },

    togglePrintFormat(event, page) {
        let analyticsName;

        if (page == 'no test' || page == 'no test selected students') {
            analyticsName = 'include-no-test-events';
        } else if (page == 'page break' || page == 'page break selected students') {
            analyticsName = '2-sided-printing';
        }
        Analytics.recordButtonClick(CommonValues.SectionIDs.printAndShare, analyticsName);

        let format = this.state.selectedPrintFormat;
        if (format.includes(page)) {
            format = format.delete(page);
        } else {
            format = format.add(page);
        }
        this.setState({
            selectedPrintFormat: format,
        });
        this.selectFormat(format);
    },

    selectFormat(format) {
        this.setState({
            selectedPrintFormat: format,
        });
    },

    toggleStudentSelection(event, students) {
        let analyticsName;

        if (students == 'current') {
            analyticsName = 'one-student';
        } else if (students == 'all') {
            analyticsName = 'all-students';
        } else {
            analyticsName = 'select-students';
        }
        Analytics.recordButtonClick(CommonValues.SectionIDs.printAndShare, 'select-students', analyticsName);

        let previousSelection = this.state.selectedStudentForPrinting;
        this.setState({
            selectedStudentForPrinting: students,
            studentPreviousSelection: previousSelection,
        });
        this.selectStudents(students);
    },

    selectStudents(students) {
        this.setState({
            studentOption: students,
        });
    },

    togglePrintAndShareText() {
        if (this.state.batchOptionState && this.state.isFamilyReport) {
            return 'PRINT AND SHARE \u2014 Family Report';
        } else if (this.state.batchOptionState && !this.state.isFamilyReport) {
            return `PRINT AND SHARE \u2014 Student Profile`;
        } else {
            return 'PRINT AND SHARE';
        }
    },

    render() {
        const displayPages = BatchUtils.displayPages;

        let switchClass =
            this.state.selectedStudentForPrinting != 'current' && this.state.studentPreviousSelection != 'current'
                ? 'switch-between-multiple-options '
                : ' ';
        let studentOptionClass = `batch ${switchClass} ${this.state.batchTab} `;

        if (
            this.state.studentOption != null &&
            this.state.studentOption == 'choose' &&
            this.state.selectedStudentForPrinting == 'choose'
        ) {
            studentOptionClass += 'select-students ';
        } else if (this.state.studentOption == 'all' && this.state.selectedStudentForPrinting == 'all') {
            studentOptionClass += 'all-students ';
        } else {
            studentOptionClass += 'one-student ';
        }

        let panelClasses;

        if (this.state.isFamilyReport) {
            panelClasses = studentOptionClass + this.state.batchTab + ' family-report';
        } else {
            panelClasses = this.state.showBatchPrint ? studentOptionClass : 'share ' + this.state.batchTab;
        }

        return (
            <span className={this.state.showPrintShare ? 'print-share-sheet active' : 'print-share-sheet'}>
                <span onClick={this.togglePrintAndShare} title="Print & Share" className="print-share-btn">
                    <i key="print-icon" className="icon icon-print-share" />
                    <span className="print-share-label" title="Print & Share">
                        PRINT & SHARE
                    </span>
                </span>
                <div id="print-share-panel" className={panelClasses}>
                    <div className="print-share-heading">
                        <h4 onClick={this.state.showBatchPrint ? this.closeBatchPrinting : () => {}}>
                            <i key="batch-arrow" className="icon icon-action-arrow-right arrow-left" />
                            {this.togglePrintAndShareText()}
                        </h4>
                        <i
                            id="close-btn-caret"
                            className="icon icon-close-caret close-caret"
                            onClick={this.togglePrintAndShare}
                            key="print-share-close-button"
                        />
                    </div>
                    <ShareOptions
                        selectBatchPrintPDF={this.selectBatchPrintPDF}
                        togglePrintAndShare={this.togglePrintAndShare}
                        isHelpBoxOpen={this.isHelpBoxOpen}
                        reportQueueLink={this.props.reportsQueueLink}
                    />
                    <BatchOptions
                        batchOptionState={this.state.batchOptionState}
                        batchTab={this.state.batchTab}
                        displayPages={displayPages}
                        isFamilyReport={this.state.isFamilyReport}
                        isHelpBoxOpen={this.isHelpBoxOpen}
                        isSingleStudentMode={this.props.isSingleStudentMode}
                        linkingStudies={this.props.linkingStudies}
                        nextButtonAction={this.nextButtonAction}
                        reportQueueLink={this.props.reportsQueueLink}
                        selectedClass={this.props.selectedClass}
                        selectedModule={this.props.selectedModule}
                        selectedPrintFormat={this.state.selectedPrintFormat}
                        selectedStudentForPrinting={this.state.selectedStudentForPrinting}
                        selectedSubject={this.props.selectedSubject}
                        selectedTermNumber={this.props.selectedTermNumber}
                        selectFormat={this.selectFormat}
                        selectStudents={this.selectStudents}
                        studentInfo={this.props.studentInfo}
                        studentPreviousSelection={this.state.studentPreviousSelection}
                        students={this.props.students}
                        termsWithTestEvents={this.props.termsWithTestEvents}
                        togglePrintAndShare={this.togglePrintAndShare}
                        togglePrintFormat={this.togglePrintFormat}
                        toggleStudentSelection={this.toggleStudentSelection}
                        visitedSections={this.state.visitedSections}
                    />
                </div>
                <div id="print-share-lightbox" />
            </span>
        );
    },
});
